import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@mui/material';
import {
  UpdateInvoicesAdditionalDetailsRequestSchema,
  type UpdateInvoicesAdditionalDetailsRequestPayload,
} from '@vy/api-definition';
import { isNotNullOrUndefined, isSingleItem } from '@vy/utils';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../../../components/new-design/feedback/VyDialog';
import { useBulkUpdateInvoiceAdditionalDetails } from '../../../../../../hooks/requests/use-bulk-update-invoice-additional-details';
import { useUpdateInvoiceAdditionalDetails } from '../../../../../../hooks/requests/use-update-invoice-additional-details';
import { queryKeys } from '../../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../../utils/vy-mutation';

type AddInvoiceNotesFormProps = {
  open: boolean;
  onClose?: () => void;
  existingNotes?: string;
  invoiceIds: string[];
};

export const AddInvoiceNotesForm = ({
  open,
  onClose,
  existingNotes,
  invoiceIds,
}: AddInvoiceNotesFormProps) => {
  const isSingleInvoice = isSingleItem(invoiceIds);
  const invoiceId = invoiceIds[0];

  const updateNotesForm = useForm<UpdateInvoicesAdditionalDetailsRequestPayload>({
    resolver: zodResolver(UpdateInvoicesAdditionalDetailsRequestSchema),
  });

  useEffect(() => {
    if (isSingleInvoice && isNotNullOrUndefined(existingNotes)) {
      updateNotesForm.reset({ notes: existingNotes, invoiceIds });
    } else {
      updateNotesForm.reset({ notes: undefined, invoiceIds });
    }
  }, [isSingleInvoice, existingNotes, invoiceId]);

  const notesInputRegister = updateNotesForm.register('notes');

  const queryClient = useQueryClient();
  const updateInvoiceAdditionalDetailsMutation = useUpdateInvoiceAdditionalDetails();
  const bulkUpdateInvoiceAdditionalDetailsMutation = useBulkUpdateInvoiceAdditionalDetails();

  const onSubmit = updateNotesForm.handleSubmit(async ({ notes }) => {
    if (isSingleInvoice) {
      await runMutation(
        updateInvoiceAdditionalDetailsMutation,
        { id: invoiceId, notes },
        {
          pending: 'Updating notes...',
          success: 'Notes updated',
          error: 'Failed to update notes',
        },
        {
          onSuccess: async () => {
            await Promise.all([
              queryClient.invalidateQueries(queryKeys.invoicePdf(invoiceId)),
              queryClient.invalidateQueries(queryKeys.dynamicInvoice(invoiceId)),
            ]);
          },
        },
      );
    } else {
      await runMutation(
        bulkUpdateInvoiceAdditionalDetailsMutation,
        { invoiceIds, notes },
        {
          pending: 'Updating notes...',
          success: 'Notes updated',
          error: 'Failed to update notes',
        },
      );
    }

    updateNotesForm.reset();
    return onClose?.();
  });

  const handleClose = () => {
    updateNotesForm.reset();
    updateNotesForm.clearErrors();
    onClose?.();
  };

  return (
    <>
      <VyDialog
        open={open}
        onClose={handleClose}
        label="Additional notes and details"
        labelDescription="The details will be added to this invoice only"
        onSubmit={onSubmit}
        ctaText="Update"
        cancelButtonText="Cancel"
      >
        <TextField
          {...notesInputRegister}
          label="Notes & details"
          placeholder="Text here"
          size="medium"
          fullWidth
          sx={{
            padding: '15px',
          }}
          error={isNotNullOrUndefined(updateNotesForm.formState.errors[notesInputRegister.name])}
          helperText={updateNotesForm.formState.errors[notesInputRegister.name]?.message}
        />
      </VyDialog>
    </>
  );
};
